/**
 * DashboardWidgetFrame — edit-mode wrapper around a dashboard widget.
 *
 * Only rendered while the dashboard is in edit mode (always inside a
 * DndContext + SortableContext), so it can safely use useSortable. It provides
 * a drag handle for reordering and a switch to show/hide the widget. Hidden
 * widgets are dimmed here (so they can be re-enabled) and simply not rendered
 * at all in normal mode by DashboardPage.
 */
import type { ReactNode } from 'react';
import {
  Box,
  FormControlLabel,
  IconButton,
  Tooltip,
  Switch,
  Typography,
  alpha,
} from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useTranslation } from 'react-i18next';

interface DashboardWidgetFrameProps {
  id: string;
  label: string;
  hidden: boolean;
  onToggleHidden: () => void;
  /** Whether the widget content stays interactive in edit mode (e.g. quick
   *  actions need their toggles clickable). Others are click-inert while
   *  editing so a stray click can't navigate away. */
  interactive?: boolean;
  children: ReactNode;
}

export function DashboardWidgetFrame({
  id,
  label,
  hidden,
  onToggleHidden,
  interactive = false,
  children,
}: DashboardWidgetFrameProps) {
  const { t } = useTranslation('dashboard');
  const { t: tc } = useTranslation('common');
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      sx={{
        mb: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        overflow: 'hidden',
        bgcolor: 'background.paper',
        opacity: isDragging ? 0.72 : 1,
        boxShadow: isDragging ? 6 : undefined,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 1,
          bgcolor: (theme) => alpha(theme.palette.text.primary, 0.03),
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Tooltip title={tc('dragToReorder')}>
          <IconButton
            ref={setActivatorNodeRef}
            {...attributes}
            {...listeners}
            size="small"
            edge="start"
            sx={{ cursor: isDragging ? 'grabbing' : 'grab' }}
            aria-label={tc('dragToReorder')}
          >
            <DragIndicatorIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Typography variant="subtitle2" sx={{ flex: 1, minWidth: 0 }} noWrap>
          {label}
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={!hidden}
              onChange={onToggleHidden}
              size="small"
            />
          }
          label={hidden ? t('editor.hidden') : t('editor.visible')}
          slotProps={{ typography: { variant: 'caption', color: 'text.secondary' } }}
          sx={{ mr: 0 }}
        />
      </Box>
      <Box
        sx={{
          p: 1.5,
          opacity: hidden ? 0.4 : 1,
          pointerEvents: hidden || !interactive ? 'none' : 'auto',
        }}
      >
        {children}
      </Box>
    </Box>
  );
}
